import * as THREE from 'three'
import type { Game, GameContext, GameMeta, InputState } from '../types'
import {
  createThreeKit,
  disposeObject,
  makeGlowDisc,
  makeStars,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit'
import { playLaunch, playLand } from '../../audio/sfx'

type Phase = 'ready' | 'angle' | 'power' | 'flight' | 'land' | 'over'

/**
 * MOMOHA CANNON — carnival power/angle timing shot.
 * FIRE locks the moving meter (angle → power → launch). Music boosts hang-time on kicks.
 */
export class MomohaCannonGame implements Game {
  readonly meta: GameMeta = {
    id: 'cannon',
    title: 'FREEMUSIERS CANNONADE',
    blurb: 'THE GREAT FREEMUSIERS CANNONADE — lock angle & power, yeet the TGN ninja. Beat hang-time!',
    tag: 'CARNIVAL',
    status: 'playable',
  }

  private ctx!: GameContext
  private kit!: ThreeKit
  private world!: THREE.Group
  private cannonPivot!: THREE.Group
  private barrel!: THREE.Group
  private momoha!: THREE.Group
  private rider!: THREE.Group
  private trail: THREE.Mesh[] = []
  private markers: THREE.Group[] = []
  private meterBg!: THREE.Mesh
  private meterFill!: THREE.Mesh
  private meterLabel!: THREE.Sprite
  private angleArrow!: THREE.Mesh

  private phase: Phase = 'ready'
  private meterT = 0
  private meterDir = 1
  private angle01 = 0.45
  private power01 = 0.5
  private lockedAngle = 0.55 // radians-ish 0..1 → deg
  private lockedPower = 0.5

  private px = 0
  private py = 0
  private vx = 0
  private vy = 0
  private flightT = 0
  private landedX = 0
  private best = 0
  private score = 0
  private lives = 5
  private wave = 1
  private message = 'FIRE · lock ANGLE then POWER'
  private messageT = 3
  private disposed = false
  private camX = 0
  private stars!: THREE.Points
  private wind = 0
  private sparks: { m: THREE.Mesh; life: number; vx: number; vy: number }[] = []

  // Parallax background layers
  private bgLayer1 = new THREE.Group() // mountains
  private bgLayer2 = new THREE.Group() // cityscape / ferris wheels
  private bgLayer3 = new THREE.Group() // close carnival tents

  private clickFire = false
  private onCanvasPointer = (e: PointerEvent) => {
    if (e.button !== 0) return
    this.clickFire = true
  }

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx
    this.kit = createThreeKit(ctx, {
      label: 'Momoha Cannon',
      z: 28,
      fov: 42,
      clear: 0x14081c,
      fog: 0x14081c,
      fogNear: 40,
      fogFar: 110,
    })
    this.kit.renderer.domElement.addEventListener('pointerdown', this.onCanvasPointer)
    this.kit.scene.add(new THREE.AmbientLight(0xffc8e8, 0.7))
    const sun = new THREE.DirectionalLight(0xffe8c8, 1.1)
    sun.position.set(-8, 20, 12)
    this.kit.scene.add(sun)
    const neon = new THREE.PointLight(0xff44aa, 1.2, 60)
    neon.position.set(0, 6, 6)
    this.kit.scene.add(neon)

    this.stars = makeStars(this.kit.lowPower ? 50 : 120, 0xff88cc)
    this.kit.scene.add(this.stars)

    this.kit.scene.add(this.bgLayer1);
    this.bgLayer1.position.z = -80;
    this.kit.scene.add(this.bgLayer2);
    this.bgLayer2.position.z = -50;
    this.kit.scene.add(this.bgLayer3);
    this.bgLayer3.position.z = -20;
    this.buildParallaxBackground();

    this.world = new THREE.Group()
    this.kit.root.add(this.world)

    // dark fairway + rainbow LED pads near booth
    const ground = new THREE.Mesh(
      new THREE.BoxGeometry(220, 0.4, 18),
      new THREE.MeshStandardMaterial({ color: 0x12101c, roughness: 0.92 }),
    )
    ground.position.set(70, -0.2, 0)
    this.world.add(ground)

    const ledColors = [0xff00aa, 0x00e5ff, 0x66ff66, 0xffee44, 0xaa66ff, 0xff6622]
    for (let i = 0; i < 14; i++) {
      for (let j = 0; j < 4; j++) {
        const c = ledColors[(i + j) % ledColors.length]
        const tile = new THREE.Mesh(
          new THREE.BoxGeometry(1.35, 0.06, 1.35),
          new THREE.MeshStandardMaterial({
            color: c,
            emissive: c,
            emissiveIntensity: 0.55,
            roughness: 0.4,
          }),
        )
        tile.position.set(-2 + i * 1.5, 0.04, -2.4 + j * 1.5)
        this.world.add(tile)
      }
    }

    // distance ticks every 10m
    for (let d = 10; d <= 200; d += 10) {
      const g = new THREE.Group()
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 1.2, 0.12),
        new THREE.MeshStandardMaterial({ color: 0xffee88, emissive: 0xaa8800, emissiveIntensity: 0.4 }),
      )
      post.position.y = 0.6
      g.add(post)
      const flag = new THREE.Mesh(
        new THREE.PlaneGeometry(1.1, 0.55),
        new THREE.MeshBasicMaterial({ color: d % 50 === 0 ? 0xff4488 : 0x44ffcc, side: THREE.DoubleSide }),
      )
      flag.position.set(0.55, 1.15, 0)
      g.add(flag)
      g.position.set(d, 0, -2.2)
      this.world.add(g)
      this.markers.push(g)
    }

    // truss arch + WELCOME TO FREEMUSIERS
    this.buildBoothArch()

    // catch net downrange (visual goal)
    this.buildCatchNet(48)

    // plaque
    const plaque = this.makeTextPlane("THE GREAT FREEMUSIERS CANNONADE\n— COLLECTOR'S AUTHENTICITY —", 512, 160, '#111018', '#ffeef8')
    plaque.scale.set(3.2, 1.0, 1)
    plaque.position.set(0.2, 0.55, 2.4)
    plaque.rotation.x = -0.35
    this.world.add(plaque)

    // Momoha (pink twin-bun operator)
    this.momoha = this.makeMomoha()
    this.momoha.position.set(-1.35, 0.5, 0.85)
    this.momoha.rotation.y = 0.35
    this.world.add(this.momoha)

    // Bronze carnival cannon
    this.cannonPivot = new THREE.Group()
    this.cannonPivot.position.set(0.55, 1.25, 0)
    this.world.add(this.cannonPivot)

    const woodMat = new THREE.MeshStandardMaterial({ color: 0x8b5a2b, roughness: 0.85, metalness: 0.05 })
    const bronzeMat = new THREE.MeshStandardMaterial({
      color: 0xb87333,
      metalness: 0.72,
      roughness: 0.38,
      emissive: 0x3a1808,
      emissiveIntensity: 0.15,
    })
    const ironMat = new THREE.MeshStandardMaterial({ color: 0x2a2420, metalness: 0.55, roughness: 0.5 })

    const carriage = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.55, 1.15), woodMat)
    carriage.position.set(-0.2, -0.55, 0)
    this.cannonPivot.add(carriage)

    this.barrel = new THREE.Group()
    const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.62, 3.4, 18), bronzeMat)
    tube.rotation.z = Math.PI / 2
    tube.position.x = 1.5
    this.barrel.add(tube)
    // iron bands
    for (const bx of [0.4, 1.5, 2.6]) {
      const band = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.06, 8, 20), ironMat)
      band.rotation.y = Math.PI / 2
      band.position.x = bx
      this.barrel.add(band)
    }
    const muzzle = new THREE.Mesh(
      new THREE.TorusGeometry(0.5, 0.09, 8, 22),
      new THREE.MeshStandardMaterial({ color: 0xd4a017, metalness: 0.6, roughness: 0.35, emissive: 0x553300, emissiveIntensity: 0.25 }),
    )
    muzzle.rotation.y = Math.PI / 2
    muzzle.position.x = 3.15
    this.barrel.add(muzzle)
    this.cannonPivot.add(this.barrel)

    // spoked wooden wheels
    for (const sz of [-0.85, 0.85]) {
      const wh = new THREE.Group()
      const tire = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.12, 8, 24), ironMat)
      tire.rotation.y = Math.PI / 2
      wh.add(tire)
      const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.2, 10), woodMat)
      hub.rotation.z = Math.PI / 2
      wh.add(hub)
      for (let s = 0; s < 8; s++) {
        const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.2, 0.08), woodMat)
        spoke.rotation.z = (s / 8) * Math.PI
        wh.add(spoke)
      }
      wh.position.set(0.15, 0.15, sz)
      this.world.add(wh)
    }

    // TGN ninja projectile
    this.rider = this.makeRider()
    this.rider.position.set(2.85, 1.45, 0)
    this.world.add(this.rider)

    // angle guide arrow
    this.angleArrow = new THREE.Mesh(
      new THREE.ConeGeometry(0.2, 0.7, 8),
      new THREE.MeshBasicMaterial({ color: 0x66ffee }),
    )
    this.angleArrow.position.set(3.5, 1.5, 0)
    this.world.add(this.angleArrow)

    // meter UI
    const meterRoot = new THREE.Group()
    meterRoot.position.set(-8, 5.5, 4)
    this.kit.scene.add(meterRoot)
    this.meterBg = new THREE.Mesh(
      new THREE.PlaneGeometry(6.5, 0.7),
      new THREE.MeshBasicMaterial({ color: 0x1a1028, transparent: true, opacity: 0.85 }),
    )
    meterRoot.add(this.meterBg)
    this.meterFill = new THREE.Mesh(
      new THREE.PlaneGeometry(6.2, 0.45),
      new THREE.MeshBasicMaterial({ color: 0xff66aa }),
    )
    this.meterFill.position.z = 0.02
    meterRoot.add(this.meterFill)
    this.meterLabel = this.makeTextSprite('ANGLE', '#ffccee')
    this.meterLabel.position.set(0, 0.85, 0)
    this.meterLabel.scale.set(3.2, 0.9, 1)
    meterRoot.add(this.meterLabel)

    const disc = makeGlowDisc(0xff66aa, 4, 0.25)
    disc.rotation.x = -Math.PI / 2
    disc.position.set(100, 0.05, 0)
    this.world.add(disc)

    this.resetRound(true)
    resizeThree(this.kit, ctx.mount.clientWidth || 800, ctx.mount.clientHeight || 450, 26, 32)
  }

  private buildParallaxBackground() {
    // Mountains (Layer 1)
    const mtnMat = new THREE.MeshBasicMaterial({ color: 0x0f0b18 });
    for (let i = 0; i < 10; i++) {
      const geo = new THREE.ConeGeometry(40 + Math.random() * 30, 30 + Math.random() * 40, 4);
      const m = new THREE.Mesh(geo, mtnMat);
      m.position.set(-50 + i * 80, 5, 0);
      m.rotation.y = Math.PI / 4;
      this.bgLayer1.add(m);
    }

    // Cityscape/Ferris Wheels (Layer 2)
    const cityMat = new THREE.MeshBasicMaterial({ color: 0x1d102e });
    for (let i = 0; i < 20; i++) {
      const isWheel = Math.random() > 0.8;
      if (isWheel) {
        const geo = new THREE.TorusGeometry(12, 0.5, 8, 16);
        const m = new THREE.Mesh(geo, cityMat);
        m.position.set(i * 60, 10, 0);
        this.bgLayer2.add(m);
      } else {
        const h = 10 + Math.random() * 25;
        const w = 8 + Math.random() * 15;
        const geo = new THREE.BoxGeometry(w, h, w);
        const m = new THREE.Mesh(geo, cityMat);
        m.position.set(-20 + i * 40, h / 2 - 2, 0);
        this.bgLayer2.add(m);
      }
    }

    // Carnival Tents (Layer 3)
    const tentColors = [0xff2d95, 0xffaa00, 0x33ff99, 0x33ddff];
    for (let i = 0; i < 30; i++) {
      const c = tentColors[i % tentColors.length];
      const mat = new THREE.MeshBasicMaterial({ color: c });
      const geo = new THREE.ConeGeometry(8, 10 + Math.random() * 6, 8);
      const m = new THREE.Mesh(geo, mat);
      m.position.set(-10 + i * 30, 2, 0);
      this.bgLayer3.add(m);
    }
  }

  private buildBoothArch() {
    const steel = new THREE.MeshStandardMaterial({ color: 0x3a3a48, metalness: 0.65, roughness: 0.4 })
    for (const x of [-5.5, 5.5]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.35, 7.5, 0.35), steel)
      leg.position.set(x, 3.75, -1.2)
      this.world.add(leg)
    }
    const beam = new THREE.Mesh(new THREE.BoxGeometry(11.5, 0.4, 0.4), steel)
    beam.position.set(0, 7.4, -1.2)
    this.world.add(beam)

    const welcome = this.makeTextPlane('WELCOME TO FREEMUSIERS', 640, 96, '#0a0612', null, true)
    welcome.scale.set(7.5, 1.15, 1)
    welcome.position.set(0, 7.9, -1.0)
    this.world.add(welcome)

    // pennants
    const pennantCols = [0xff4488, 0x44ffcc, 0xffee44, 0x6688ff, 0xff8844]
    for (let i = 0; i < 14; i++) {
      const p = new THREE.Mesh(
        new THREE.ConeGeometry(0.22, 0.55, 3),
        new THREE.MeshBasicMaterial({ color: pennantCols[i % pennantCols.length] }),
      )
      p.rotation.z = Math.PI
      p.position.set(-5 + i * 0.8, 7.05, -0.9)
      this.world.add(p)
    }

    // purple banners
    const b1 = this.makeTextPlane('AI IS MY MUSE', 256, 512, '#2a0040', '#ffffff')
    b1.scale.set(1.4, 2.8, 1)
    b1.position.set(-5.2, 4.2, -0.6)
    this.world.add(b1)
    const b2 = this.makeTextPlane('CREATIVITY\nPARADOX', 256, 400, '#2a0040', '#ffccee')
    b2.scale.set(1.3, 2.2, 1)
    b2.position.set(-5.2, 2.0, -0.55)
    this.world.add(b2)
  }

  private buildCatchNet(atX: number) {
    const steel = new THREE.MeshStandardMaterial({ color: 0x555566, metalness: 0.5, roughness: 0.45 })
    for (const z of [-2.8, 2.8]) {
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 5.5, 8), steel)
      pole.position.set(atX, 2.75, z)
      this.world.add(pole)
    }
    const net = new THREE.Mesh(
      new THREE.PlaneGeometry(5.2, 4.8, 12, 10),
      new THREE.MeshBasicMaterial({
        color: 0xeeeeff,
        wireframe: true,
        transparent: true,
        opacity: 0.55,
      }),
    )
    net.position.set(atX, 2.8, 0)
    net.rotation.y = Math.PI / 2
    this.world.add(net)
  }

  private makeMomoha(): THREE.Group {
    const g = new THREE.Group()
    const skin = new THREE.MeshStandardMaterial({ color: 0xffe4dc, roughness: 0.65 })
    const pink = new THREE.MeshStandardMaterial({
      color: 0xff3d9a,
      emissive: 0x881144,
      emissiveIntensity: 0.35,
      roughness: 0.55,
    })
    // black FREEMUSIERS hoodie
    const body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.38, 0.5, 6, 12),
      new THREE.MeshStandardMaterial({ color: 0x141418, roughness: 0.75 }),
    )
    body.position.y = 0.85
    g.add(body)
    const hoodText = this.makeTextPlane('FREEMUSIERS', 256, 64, 'transparent', '#ffffff')
    hoodText.scale.set(0.9, 0.22, 1)
    hoodText.position.set(0, 1.05, 0.36)
    g.add(hoodText)

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.4, 14, 12), skin)
    head.position.y = 1.58
    g.add(head)
    // twin high buns
    for (const sx of [-1, 1]) {
      const bun = new THREE.Mesh(new THREE.SphereGeometry(0.22, 12, 10), pink)
      bun.position.set(sx * 0.32, 1.95, -0.05)
      g.add(bun)
      const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 0.2, 8), pink)
      stem.position.set(sx * 0.28, 1.78, -0.02)
      g.add(stem)
      // star clip
      const star = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.07),
        new THREE.MeshBasicMaterial({ color: 0xffee66 }),
      )
      star.position.set(sx * 0.38, 1.88, 0.12)
      g.add(star)
    }
    // side hair
    const fringe = new THREE.Mesh(new THREE.SphereGeometry(0.42, 12, 10, 0, Math.PI * 2, 0, Math.PI * 0.55), pink)
    fringe.position.set(0, 1.62, 0)
    g.add(fringe)
    // eyes
    for (const s of [-1, 1]) {
      const white = new THREE.Mesh(
        new THREE.SphereGeometry(0.09, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xffffff }),
      )
      white.position.set(s * 0.13, 1.6, 0.34)
      g.add(white)
      const iris = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xff2266 }),
      )
      iris.position.set(s * 0.13, 1.6, 0.4)
      g.add(iris)
    }
    // arms on cannon lean
    const armL = new THREE.Mesh(new THREE.CapsuleGeometry(0.09, 0.4, 4, 8), skin)
    armL.position.set(-0.35, 1.05, 0.25)
    armL.rotation.z = 0.9
    armL.rotation.x = -0.4
    g.add(armL)
    const armR = new THREE.Mesh(new THREE.CapsuleGeometry(0.09, 0.4, 4, 8), skin)
    armR.position.set(0.45, 1.0, 0.15)
    armR.rotation.z = -0.85
    armR.name = 'arm'
    g.add(armR)
    return g
  }

  private makeRider(): THREE.Group {
    // TGN ninja — black suit, red scarf, blue headband
    const g = new THREE.Group()
    const black = new THREE.MeshStandardMaterial({ color: 0x121218, roughness: 0.55 })
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.26, 0.45, 6, 10), black)
    body.position.y = 0.45
    g.add(body)
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.24, 12, 10), black)
    head.position.y = 0.95
    g.add(head)
    // blue TGN headband
    const band = new THREE.Mesh(
      new THREE.TorusGeometry(0.25, 0.05, 6, 16),
      new THREE.MeshStandardMaterial({ color: 0x1a8cff, emissive: 0x0044aa, emissiveIntensity: 0.45 }),
    )
    band.rotation.x = Math.PI / 2
    band.position.y = 1.05
    g.add(band)
    const tgn = this.makeTextSprite('TGN', '#ffffff')
    tgn.scale.set(0.55, 0.2, 1)
    tgn.position.set(0, 1.12, 0.22)
    g.add(tgn)
    // red scarf
    const scarf = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, 0.08, 0.9),
      new THREE.MeshStandardMaterial({ color: 0xff2244, emissive: 0x660010, emissiveIntensity: 0.35 }),
    )
    scarf.position.set(0, 0.75, -0.35)
    scarf.rotation.x = 0.35
    scarf.name = 'scarf'
    g.add(scarf)
    // dice orbiters (shown in flight)
    for (let i = 0; i < 3; i++) {
      const die = new THREE.Mesh(
        new THREE.BoxGeometry(0.16, 0.16, 0.16),
        new THREE.MeshStandardMaterial({
          color: i === 0 ? 0xff3344 : i === 1 ? 0x3388ff : 0xffffff,
          roughness: 0.4,
        }),
      )
      die.position.set(Math.cos(i * 2) * 0.45, 0.5 + i * 0.15, Math.sin(i * 2) * 0.45)
      die.name = `die${i}`
      g.add(die)
    }
    return g
  }

  private makeTextPlane(
    text: string,
    w: number,
    h: number,
    bg: string,
    fg: string | null,
    rainbow = false,
  ): THREE.Mesh {
    const c = document.createElement('canvas')
    c.width = w
    c.height = h
    const ctx = c.getContext('2d')!
    if (bg !== 'transparent') {
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, w, h)
    } else {
      ctx.clearRect(0, 0, w, h)
    }
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const lines = text.split('\n')
    const size = Math.floor(h / (lines.length + 1.2))
    ctx.font = `bold ${size}px system-ui,sans-serif`
    lines.forEach((line, i) => {
      const y = h / 2 + (i - (lines.length - 1) / 2) * size * 1.15
      if (rainbow) {
        const grd = ctx.createLinearGradient(0, 0, w, 0)
        grd.addColorStop(0, '#ff2d95')
        grd.addColorStop(0.2, '#ffaa00')
        grd.addColorStop(0.4, '#ffe600')
        grd.addColorStop(0.55, '#33ff99')
        grd.addColorStop(0.7, '#33ddff')
        grd.addColorStop(0.85, '#6688ff')
        grd.addColorStop(1, '#cc66ff')
        ctx.fillStyle = grd
      } else {
        ctx.fillStyle = fg || '#ffffff'
      }
      ctx.fillText(line, w / 2, y)
    })
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Mesh(
      new THREE.PlaneGeometry(1, h / w),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide }),
    )
  }

  private makeTextSprite(text: string, color: string): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = 256
    c.height = 64
    const ctx = c.getContext('2d')!
    ctx.clearRect(0, 0, 256, 64)
    ctx.font = 'bold 36px system-ui,sans-serif'
    ctx.fillStyle = color
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, 128, 34)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }))
  }

  private setMeterLabel(text: string, color: string) {
    const mat = this.meterLabel.material as THREE.SpriteMaterial
    mat.map?.dispose()
    const spr = this.makeTextSprite(text, color)
    mat.map = (spr.material as THREE.SpriteMaterial).map
    mat.needsUpdate = true
    ;(spr.material as THREE.Material).dispose()
  }

  private resetRound(full = false) {
    if (full) {
      this.lives = 5
      this.wave = 1
      this.score = 0
      this.best = Math.max(this.best, 0)
    }
    this.phase = 'ready'
    this.meterT = 0
    this.meterDir = 1
    this.px = 0
    this.py = 0
    this.vx = 0
    this.vy = 0
    this.flightT = 0
    this.camX = 0
    this.rider.visible = true
    this.rider.position.set(2.6, 1.35, 0)
    this.rider.rotation.set(0, 0, 0)
    this.cannonPivot.rotation.z = 0
    this.clearTrail()
    this.message = 'FIRE to start · ANGLE then POWER'
    this.messageT = 2.5
    this.setMeterLabel('READY', '#ffccee')
    ;(this.meterFill.material as THREE.MeshBasicMaterial).color.setHex(0x8866aa)
    this.meterFill.scale.set(0.05, 1, 1)
  }

  private clearTrail() {
    for (const t of this.trail) {
      this.world.remove(t)
      t.geometry.dispose()
      ;(t.material as THREE.Material).dispose()
    }
    this.trail = []
  }

  private startAngle() {
    this.phase = 'angle'
    this.meterT = Math.random() * 0.6
    this.meterDir = 1
    this.message = 'CLICK / FIRE — lock ANGLE'
    this.messageT = 2
    this.setMeterLabel('ANGLE', '#66ffee')
    ;(this.meterFill.material as THREE.MeshBasicMaterial).color.setHex(0x66ffee)
  }

  private startPower() {
    this.phase = 'power'
    this.meterT = Math.random() * 0.5
    this.meterDir = 1
    this.message = 'CLICK / FIRE — lock POWER'
    this.messageT = 2
    this.setMeterLabel('POWER', '#ff66aa')
    ;(this.meterFill.material as THREE.MeshBasicMaterial).color.setHex(0xff66aa)
  }

  private launch() {
    this.phase = 'flight'
    this.flightT = 0
    // angle: 18° .. 72°
    const deg = 18 + this.lockedAngle * 54
    const rad = (deg * Math.PI) / 180
    // power → speed
    const bands = this.ctx.getBands()
    const musicBoost = 1 + bands.energy * 0.15
    const speed = (14 + this.lockedPower * 38) * musicBoost
    this.vx = Math.cos(rad) * speed
    this.vy = Math.sin(rad) * speed
    // barrel mouth
    const mouth = new THREE.Vector3(3.0, 0, 0)
    this.barrel.localToWorld(mouth)
    this.world.worldToLocal(mouth)
    this.px = mouth.x
    this.py = Math.max(0.6, mouth.y)
    this.rider.position.set(this.px, this.py, 0)
    this.rider.visible = true
    this.message = '✈ YEET'
    this.messageT = 1.2
    this.setMeterLabel('FLIGHT', '#ffe066')

    playLaunch()
    this.spawnBurst(this.px, this.py)
    this.spawnSmoke(this.px, this.py)

    // Momoha arm cheer
    const arm = this.momoha.getObjectByName('arm')
    if (arm) arm.rotation.z = -1.4
  }

  private spawnSmoke(x: number, y: number) {
    const n = this.kit.lowPower ? 12 : 24
    for (let i = 0; i < n; i++) {
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(0.5 + Math.random() * 1.5, 6, 6),
        new THREE.MeshBasicMaterial({ color: 0x888899, transparent: true, opacity: 0.8 }),
      )
      m.position.set(x + (Math.random() - 0.5) * 2, y + (Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2)
      this.world.add(m)
      this.sparks.push({
        m,
        life: 0.6 + Math.random() * 0.8,
        vx: 5 + Math.random() * 10,
        vy: (Math.random() - 0.5) * 5,
      })
    }
  }

  private spawnBurst(x: number, y: number) {
    const n = this.kit.lowPower ? 8 : 16
    for (let i = 0; i < n; i++) {
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(0.08 + Math.random() * 0.08, 6, 6),
        new THREE.MeshBasicMaterial({ color: Math.random() > 0.5 ? 0xff66aa : 0xffee66 }),
      )
      m.position.set(x, y, 0)
      this.world.add(m)
      this.sparks.push({
        m,
        life: 0.4 + Math.random() * 0.4,
        vx: (Math.random() - 0.3) * 10,
        vy: Math.random() * 8,
      })
    }
  }

  private land() {
    this.phase = 'land'
    this.landedX = Math.max(0, this.px)
    const dist = Math.round(this.landedX * 10) / 10
    this.score = Math.max(this.score, Math.floor(dist * 10))
    if (dist > this.best) this.best = dist
    this.lives -= 1
    this.wave += 1
    this.message = `LAND ${dist.toFixed(1)}m · BEST ${this.best.toFixed(1)}m · FIRE again`
    this.messageT = 4
    this.setMeterLabel(`${dist.toFixed(0)}m`, '#ffe066')
    playLand()
    this.spawnSmoke(this.landedX, 0)
    if (this.lives <= 0) {
      this.phase = 'over'
      this.message = `GAME OVER · BEST ${this.best.toFixed(1)}m · FIRE retry`
      this.messageT = 99
    }
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return
    const bands = this.ctx.getBands()
    this.wind = Math.sin(performance.now() / 900) * 0.35 * (0.4 + bands.mids)

    if (this.messageT > 0) this.messageT -= dt

    // Momoha idle bounce
    this.momoha.position.y = 0.5 + Math.sin(performance.now() / 280) * 0.05
    const arm = this.momoha.getObjectByName('arm')
    if (arm && this.phase !== 'flight') {
      arm.rotation.z = -0.5 + Math.sin(performance.now() / 220) * 0.25
    }

    // stars drift
    this.stars.rotation.y += dt * 0.02

    // sparks / smoke / wind lines
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i]
      s.life -= dt
      s.m.position.x += s.vx * dt
      s.m.position.y += s.vy * dt

      const isLine = s.m.geometry.type === 'BoxGeometry';

      if (isLine) {
         // speed lines just fly backward relative to camera/player
         const mat = s.m.material as THREE.MeshBasicMaterial;
         mat.opacity -= dt;
      } else {
        // only apply gravity to small sparks (yellow/pink), not smoke
        const mat = s.m.material as THREE.MeshBasicMaterial;
        if (mat.color.getHex() !== 0x888899) {
          s.vy -= 18 * dt
        } else {
          mat.opacity -= dt * 1.5;
          s.vx *= 0.95;
        }
      }

      const mat = s.m.material as THREE.MeshBasicMaterial;
      if (s.life <= 0 || mat.opacity <= 0) {
        this.world.remove(s.m)
        s.m.geometry.dispose()
        ;(s.m.material as THREE.Material).dispose()
        this.sparks.splice(i, 1)
      }
    }

    // meter oscillation
    if (this.phase === 'angle' || this.phase === 'power') {
      // speed scales with music energy slightly
      const spd = 1.35 + bands.energy * 0.9 + (this.phase === 'power' ? 0.25 : 0)
      this.meterT += this.meterDir * spd * dt
      if (this.meterT >= 1) {
        this.meterT = 1
        this.meterDir = -1
      } else if (this.meterT <= 0) {
        this.meterT = 0
        this.meterDir = 1
      }
      // ease toward center = higher skill sweet spot? Actually pure triangle; sweet is mid-high
      const v = this.meterT
      this.meterFill.scale.set(Math.max(0.04, v), 1, 1)
      this.meterFill.position.x = (v - 1) * 3.1

      if (this.phase === 'angle') {
        this.angle01 = v
        const deg = 18 + v * 54
        this.cannonPivot.rotation.z = (deg * Math.PI) / 180
        this.updateAngleArrow(deg)
      } else {
        this.power01 = v
      }
    }

    // input — pad/space FIRE or canvas click (carnival timing)
    const fire = input.firePressed || this.clickFire
    this.clickFire = false
    if (fire) {
      if (this.phase === 'ready') this.startAngle()
      else if (this.phase === 'angle') {
        this.lockedAngle = this.angle01
        this.startPower()
      } else if (this.phase === 'power') {
        this.lockedPower = this.power01
        this.launch()
      } else if (this.phase === 'land') this.resetRound(false)
      else if (this.phase === 'over') this.resetRound(true)
    }

    // flight physics
    if (this.phase === 'flight') {
      this.flightT += dt
      const g = 18.5 - bands.bass * 2.5 // kick = hang time
      this.vy -= g * dt
      this.vx += this.wind * dt * 2
      // beat pop
      if (bands.beat || bands.pulse > 0.85) {
        this.vy += 1.8 * bands.kick
        this.spawnBurst(this.px, this.py)
      }
      this.px += this.vx * dt
      this.py += this.vy * dt
      this.rider.position.set(this.px, this.py, 0)
      this.rider.rotation.z -= dt * 4

      // Wind speed lines
      if (this.vx > 10 && Math.random() > 0.3) {
        const line = new THREE.Mesh(
          new THREE.BoxGeometry(2 + Math.random() * 4, 0.05, 0.05),
          new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6 })
        );
        line.position.set(
          this.px + 5 + Math.random() * 10,
          this.py + (Math.random() - 0.5) * 6,
          (Math.random() - 0.5) * 2
        );
        this.world.add(line);
        this.sparks.push({
          m: line,
          life: 1.0,
          vx: -this.vx * 1.5,
          vy: 0
        });
      }

      // trail
      if (this.trail.length < 60 && Math.random() > 0.2) {
        const t = new THREE.Mesh(
          new THREE.SphereGeometry(0.15 + Math.random() * 0.1, 6, 6),
          new THREE.MeshBasicMaterial({
            color: 0xff88cc,
            transparent: true,
            opacity: 0.75,
          }),
        )
        t.position.set(this.px, this.py, 0)
        this.world.add(t)
        this.trail.push(t)
      }
      for (let i = this.trail.length - 1; i >= 0; i--) {
        const t = this.trail[i]
        const m = t.material as THREE.MeshBasicMaterial
        m.opacity *= 0.95
        t.scale.multiplyScalar(0.95)
        if (m.opacity < 0.05) {
          this.world.remove(t)
          t.geometry.dispose()
          m.dispose()
          this.trail.splice(i, 1)
        }
      }
      if (this.py <= 0.35 && this.flightT > 0.15) {
        this.py = 0.35
        this.rider.position.y = this.py
        this.land()
      }
      // safety
      if (this.px > 210 || this.py < -5) this.land()
    }

    // camera follow
    const targetCam =
      this.phase === 'flight' || this.phase === 'land' || this.phase === 'over'
        ? this.px * 0.92
        : 2
    this.camX += (targetCam - this.camX) * Math.min(1, dt * 3.5)
    if (this.kit.camera instanceof THREE.PerspectiveCamera) {
      this.kit.camera.position.set(this.camX + 2, 6.5 + Math.min(4, this.py * 0.15), 22)
      this.kit.camera.lookAt(this.camX + 4, 2.2, 0)
    }

    // apply parallax
    this.bgLayer1.position.x = -this.camX * 0.1;
    this.bgLayer2.position.x = -this.camX * 0.25;
    this.bgLayer3.position.x = -this.camX * 0.5;

    // meter follows camera a bit
    this.meterBg.parent!.position.set(this.camX - 6, 8.2, 6)

    this.kit.renderer.render(this.kit.scene, this.kit.camera)
  }

  private updateAngleArrow(deg: number) {
    const rad = (deg * Math.PI) / 180
    this.angleArrow.position.set(Math.cos(rad) * 4.2, 1.1 + Math.sin(rad) * 4.2, 0)
    this.angleArrow.rotation.z = rad - Math.PI / 2
  }

  resize(w: number, h: number): void {
    if (!this.kit) return
    resizeThree(this.kit, w, h, 24, 30)
  }

  dispose(): void {
    this.disposed = true
    this.kit?.renderer.domElement.removeEventListener('pointerdown', this.onCanvasPointer)
    this.clearTrail()
    for (const s of this.sparks) {
      this.world?.remove(s.m)
      s.m.geometry.dispose()
      ;(s.m.material as THREE.Material).dispose()
    }
    this.sparks = []
    if (this.kit) {
      disposeObject(this.kit.root)
      this.kit.dispose()
    }
  }

  getHud() {
    return {
      score: this.score,
      lives: Math.max(0, this.lives),
      wave: this.wave,
      message: this.messageT > 0 ? this.message : this.phaseHud(),
    }
  }

  private phaseHud(): string {
    switch (this.phase) {
      case 'ready':
        return 'FIRE · start shot'
      case 'angle':
        return `ANGLE ${(18 + this.angle01 * 54).toFixed(0)}°`
      case 'power':
        return `POWER ${(this.power01 * 100).toFixed(0)}%`
      case 'flight':
        return `${this.px.toFixed(0)}m`
      case 'land':
        return `BEST ${this.best.toFixed(1)}m · FIRE`
      case 'over':
        return `BEST ${this.best.toFixed(1)}m · FIRE retry`
      default:
        return ''
    }
  }
}
